import { useBoolean, useOutsideClick } from '@chakra-ui/hooks'
import { Tooltip } from '@chakra-ui/tooltip'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'react-use'
import { TextButton } from '~/components/shared/TextButton'
import { hasValue } from '~/utils/common'

import { TextButtonVariants } from '../componentStyles/TextButton.style'
import { SearchInput } from '../SearchInput'
import { Menu, MenuOptions, MenuPositioningWrapper } from './Menu'
import { MenuItem } from './MenuItem'

const SEARCH_DEBOUNCE_RATE_MS = 300

export type TextButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type SearchableDropdownTextButtonProps<TValue> = {
  children?: ReactNode
  tooltipLabel?: string
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  onButtonClick?: () => void
  items: TextButtonMenuItem<TValue>[]
  defaultItem?: TextButtonMenuItem<TValue>
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => boolean | undefined
  onScrollEndReached?: () => void
  onSearchChanged?: (searchValue: string) => void
  menuOptions?: MenuOptions
  placeholder?: string
}

export const SearchableDropdownTextButton = <
  TValue extends string | number | object | never,
>({
  children,
  placeholder,
  tooltipLabel,
  leftIcon,
  rightIcon,
  onButtonClick,
  items,
  defaultItem,
  onSelect,
  onMenuOpenChange,
  onScrollEndReached,
  onSearchChanged,
  menuOptions,
}: SearchableDropdownTextButtonProps<TValue>) => {
  const [selectedItem, setSelectedItem] = useState<
    TextButtonMenuItem<TValue> | undefined
  >(defaultItem)
  const [menuOpen, setMenuOpen] = useBoolean(false)
  const [unformattedInputValue, setUnformattedInputValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState({ term: '' })

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLDivElement>(null)

  const [, cancelDebounce] = useDebounce(
    () => {
      const trimmed = unformattedInputValue.trim()
      setSearchValue({ term: trimmed })
    },
    SEARCH_DEBOUNCE_RATE_MS,
    [unformattedInputValue],
  )

  useEffect(() => {
    if (items.length > 0 && !selectedItem) {
      setSelectedItem(items[0])
      onSelect?.(items[0].value, 0)
    }
  }, [items])

  //on mount, don't update search value
  useEffect(() => {
    cancelDebounce()

    return () => {
      cancelDebounce()
    }
  }, [])

  useEffect(() => {
    onSearchChanged?.(searchValue.term)
  }, [searchValue])

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (menuOpen) {
        setMenuOpen.off()
        onMenuOpenChange?.(false)
      }
    },
  })

  const buttonLabel = useMemo(() => {
    return (
      selectedItem?.buttonLabel ??
      selectedItem?.content ??
      children ??
      placeholder
    )
  }, [items, selectedItem])

  const handleButtonClick = () => {
    const didMenuOpenChangeCancel = onMenuOpenChange?.(!menuOpen) === false
    if (!menuOpen && didMenuOpenChangeCancel) {
      // The handler returned false, cancelling the open action
      return
    }

    setMenuOpen.toggle()
    onMenuOpenChange?.(!menuOpen)
    onButtonClick?.()
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setSelectedItem(items[index])
    setMenuOpen.off()
    onMenuOpenChange?.(false)
  }

  return (
    <MenuPositioningWrapper ref={containerRef} marginLeft={-5}>
      <Tooltip
        hasArrow
        label={tooltipLabel}
        whiteSpace="pre-wrap"
        placement="top"
        isDisabled={!hasValue(tooltipLabel)}
      >
        <TextButton
          ref={buttonRef}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          onLinkClick={handleButtonClick}
          iconSize="0.75rem"
          variant={TextButtonVariants.Gray_900}
        >
          {buttonLabel}
        </TextButton>
      </Tooltip>
      {menuOpen && (
        <Menu
          anchorRef={buttonRef}
          items={items}
          onSelect={handleSelect}
          options={menuOptions}
          onScrollEndReached={onScrollEndReached}
          header={
            <SearchInput
              placeholder="Search Numbers"
              onChange={(searchValue) => {
                setUnformattedInputValue(searchValue.value)
              }}
            />
          }
          headerWrapperVariant="divided"
        />
      )}
    </MenuPositioningWrapper>
  )
}
